/**
 * Pratt parser of the expression language (W1-10c core-expr; contract src/core/expr/types.ts).
 *
 *   program := [target] [assignOp] expr | ('*' | '/') expr
 *   target  := 'v'                     (`v = ...`, `v += ...`, `v := ...` as shown by the Distribute dialog)
 *   assignOp:= '=' | ':=' | '+=' | '-=' | '*=' | '/='       (a bare expression means '=')
 *
 * A program that starts with `*` or `/` is shorthand for `*=` / `/=` (`*-1` negates, `/2` halves; D§4.11).
 *
 * Precedence, loosest first: `+ -` (left) < `* / %` (left) < unary `- +` < `^` (right). So `-2^2 = -4`,
 * `2^-1 = 0.5`, `2^3^2 = 2^9`, `-a*b = (-a)*b`. Function calls: `name(arg, ...)`, possibly without arguments.
 *
 * The result is a raw syntax tree (names unresolved); `check.ts` validates names against the variable set,
 * function arities and units, and lowers it for evaluation. `print.ts` prints raw trees canonically.
 *
 * The variable set, when known, only refines the advice for a number followed by a lone `s`, `v` or `b`: the
 * unit written apart (`2 b` -> "write 2b"), a multiplication (`2 v` on a plain field -> "write 2 * v"), or
 * either when the letter is both a unit and a variable there (`2 s` on a note, `2 v` on a key).
 */

import { EXPR_MSG, ExprFailure, fail, type Span } from './errors';
import { lex, startsOperand, type BinOpChar, type Token } from './lexer';
import type { AssignOp, ExprError, ExprVarSet } from './types';
import type { Result } from '../util/types';
import { findVar, SPEED_UNIT_SETS } from './vars';

export type RawNode =
  | (Span & {
      readonly t: 'num';
      readonly value: number;
      readonly unit: string | null;
      readonly unitFrom: number;
      readonly numTo: number;
    })
  | (Span & { readonly t: 'id'; readonly name: string })
  | (Span & { readonly t: 'str'; readonly value: string })
  | (Span & { readonly t: 'neg'; readonly arg: RawNode })
  | (Span & { readonly t: 'bin'; readonly op: BinOpChar; readonly a: RawNode; readonly b: RawNode })
  | (Span & { readonly t: 'call'; readonly name: string; readonly nameTo: number; readonly args: readonly RawNode[] });

export interface RawProgram {
  readonly assign: AssignOp;
  /** Range of the assignment prefix (including a `v` target), or null for a bare expression. */
  readonly assignSpan: Span | null;
  /** True when the prefix names its target (`v = ...`, `v += ...`). */
  readonly target: boolean;
  readonly body: RawNode;
}

/** Binding powers. Unary operands bind at UNARY_BP: tighter than `* / %`, looser than `^`. */
const LBP: Readonly<Record<BinOpChar, number>> = { '+': 10, '-': 10, '*': 20, '/': 20, '%': 20, '^': 30 };
const UNARY_BP = 25;
/** Nesting limit (parentheses, calls, unary chains) so pathological input cannot overflow the parser's stack. */
export const MAX_DEPTH = 200;
/**
 * Tree height limit: bounds the recursion of checking, evaluating and printing, including long flat chains
 * such as `1 + 1 + ... + 1` that the nesting limit does not see.
 */
export const MAX_HEIGHT = 500;

class Parser {
  private pos = 0;
  private readonly src: string;
  private readonly tokens: readonly Token[];
  private readonly heights = new WeakMap<RawNode, number>();
  private readonly vars: ExprVarSet | null;

  constructor(src: string, tokens: readonly Token[], vars: ExprVarSet | null) {
    this.src = src;
    this.tokens = tokens;
    this.vars = vars;
  }

  peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  next(): Token | undefined {
    const t = this.tokens[this.pos];
    if (t !== undefined) this.pos++;
    return t;
  }

  atEnd(): boolean {
    return this.pos >= this.tokens.length;
  }

  text(span: Span): string {
    return this.src.slice(span.from, span.to);
  }

  private height(n: RawNode): number {
    return this.heights.get(n) ?? 1;
  }

  /** Records the height of a new inner node; refuses trees taller than MAX_HEIGHT. */
  private tall<N extends RawNode>(n: N, height: number, at: Span): N {
    if (height > MAX_HEIGHT) fail(EXPR_MSG.tooDeep, at);
    this.heights.set(n, height);
    return n;
  }

  /** Range used for "something is missing at the end": the last token (usually a dangling operator). */
  endSpan(): Span {
    const last = this.tokens[this.tokens.length - 1];
    return last ?? { from: 0, to: this.src.length };
  }

  program(): RawProgram {
    const t0 = this.tokens[0];
    const t1 = this.tokens[1];
    if (t0 === undefined) fail(EXPR_MSG.empty, { from: 0, to: this.src.length });
    let assign: AssignOp = '=';
    let assignSpan: Span | null = null;
    let target = false;
    if (t0.kind === 'ident' && t0.name.toLowerCase() === 'v' && t1?.kind === 'assign') {
      assign = t1.op;
      assignSpan = { from: t0.from, to: t1.to };
      target = true;
      this.pos = 2;
    } else if (t0.kind === 'assign') {
      assign = t0.op;
      assignSpan = { from: t0.from, to: t0.to };
      this.pos = 1;
    } else if (t0.kind === 'op' && (t0.op === '*' || t0.op === '/')) {
      assign = t0.op === '*' ? '*=' : '/=';
      assignSpan = { from: t0.from, to: t0.to };
      this.pos = 1;
    }
    if (this.atEnd() && assignSpan !== null) {
      fail(EXPR_MSG.emptyAfterAssign, assignSpan, { op: assign });
    }
    const body = this.expression();
    return { assign, assignSpan, target, body };
  }

  /** A whole expression followed by the end of the input. */
  expression(): RawNode {
    const body = this.expr(0, 0);
    const rest = this.peek();
    if (rest !== undefined) this.trailing(rest);
    return body;
  }

  private trailing(t: Token): never {
    if (t.kind === ')') fail(EXPR_MSG.unmatchedParen, t);
    if (t.kind === ',') fail(EXPR_MSG.strayComma, t);
    fail(EXPR_MSG.unexpectedToken, t, { token: this.text(t) });
  }

  private expr(rbp: number, depth: number): RawNode {
    if (depth > MAX_DEPTH) fail(EXPR_MSG.tooDeep, this.peek() ?? this.endSpan());
    let left = this.nud(depth);
    for (;;) {
      const t = this.peek();
      if (t === undefined) break;
      if (t.kind === 'op') {
        const lbp = LBP[t.op];
        if (lbp <= rbp) break;
        this.next();
        const right = this.expr(t.op === '^' ? lbp - 1 : lbp, depth + 1);
        const height = Math.max(this.height(left), this.height(right)) + 1;
        left = this.tall({ t: 'bin', op: t.op, a: left, b: right, from: left.from, to: right.to }, height, t);
        continue;
      }
      if (t.kind === 'assign') fail(EXPR_MSG.assignNotFirst, t, { op: t.op });
      if (startsOperand(t)) this.missingOperator(left, t);
      break;
    }
    return left;
  }

  private missingOperator(left: RawNode, t: Token): never {
    // "2 s", "3 v", "4 b": a unit separated from its number, or a multiplication without its operator.
    if (left.t === 'num' && left.unit === null && t.kind === 'ident' && /^(s|v|b)$/i.test(t.name)) {
      const span = { from: left.from, to: t.to };
      const num = this.text(left);
      const name = t.name.toLowerCase();
      const example = `${num}${t.name}`;
      const vars = this.vars;
      if (vars !== null) {
        const unitHere = name !== 'v' || SPEED_UNIT_SETS.has(vars);
        const variableHere = findVar(vars, name) !== undefined;
        if (unitHere && variableHere) fail(EXPR_MSG.unitSpaceOrMul, span, { example, num, name });
        if (variableHere) fail(EXPR_MSG.implicitMul, span, { num, name });
        if (!unitHere) fail(EXPR_MSG.missingOperator, t, { token: this.text(t) });
      }
      fail(EXPR_MSG.unitSpace, span, { example });
    }
    fail(EXPR_MSG.missingOperator, t, { token: this.text(t) });
  }

  private nud(depth: number): RawNode {
    const t = this.next();
    if (t === undefined) fail(EXPR_MSG.unexpectedEnd, this.endSpan());
    switch (t.kind) {
      case 'num':
        return { t: 'num', value: t.value, unit: t.unit, unitFrom: t.unitFrom, numTo: t.numTo, from: t.from, to: t.to };
      case 'ident': {
        if (this.peek()?.kind === '(') return this.call(t.name, t, depth);
        return { t: 'id', name: t.name, from: t.from, to: t.to };
      }
      case 'str':
        return { t: 'str', value: t.value, from: t.from, to: t.to };
      case '(': {
        const inner = this.expr(0, depth + 1);
        const close = this.peek();
        if (close === undefined) fail(EXPR_MSG.unclosedParen, t);
        if (close.kind === ',') fail(EXPR_MSG.strayComma, close);
        if (close.kind !== ')') fail(EXPR_MSG.unexpectedToken, close, { token: this.text(close) });
        this.next();
        return this.tall({ ...inner, from: t.from, to: close.to }, this.height(inner), t);
      }
      case 'op': {
        if (t.op === '-' || t.op === '+') {
          if (this.atEnd()) fail(EXPR_MSG.unexpectedEnd, t);
          const arg = this.expr(UNARY_BP, depth + 1);
          const h = this.height(arg);
          return t.op === '-'
            ? this.tall({ t: 'neg', arg, from: t.from, to: arg.to }, h + 1, t)
            : this.tall({ ...arg, from: t.from }, h, t);
        }
        return fail(EXPR_MSG.expectedValue, t, { token: this.text(t) });
      }
      case 'assign':
        return fail(EXPR_MSG.assignNotFirst, t, { op: t.op });
      case ')':
      case ',':
        return fail(EXPR_MSG.expectedValue, t, { token: this.text(t) });
    }
  }

  private call(name: string, nameTok: Token, depth: number): RawNode {
    const open = this.next();
    if (open === undefined) return fail(EXPR_MSG.unexpectedEnd, nameTok);
    if (depth > MAX_DEPTH) fail(EXPR_MSG.tooDeep, open);
    const args: RawNode[] = [];
    const first = this.peek();
    if (first?.kind === ')') {
      this.next();
      return { t: 'call', name, nameTo: nameTok.to, args, from: nameTok.from, to: first.to };
    }
    for (;;) {
      if (this.atEnd()) fail(EXPR_MSG.unclosedParen, open);
      args.push(this.expr(0, depth + 1));
      const p = this.peek();
      if (p === undefined) fail(EXPR_MSG.unclosedParen, open);
      if (p.kind === ',') {
        this.next();
        continue;
      }
      if (p.kind === ')') {
        this.next();
        // A loop, not Math.max(...args): a call with a huge number of arguments must not overflow the stack.
        let height = 0;
        for (const a of args) height = Math.max(height, this.height(a));
        height += 1;
        return this.tall({ t: 'call', name, nameTo: nameTok.to, args, from: nameTok.from, to: p.to }, height, open);
      }
      fail(EXPR_MSG.unexpectedToken, p, { token: this.text(p) });
    }
  }
}

function attempt<T>(run: () => T): Result<T, ExprError> {
  try {
    return { ok: true, value: run() };
  } catch (e) {
    if (e instanceof ExprFailure) return { ok: false, error: e.error };
    throw e;
  }
}

/**
 * Parses a whole program (assignment prefix + expression) into a raw tree. `vars`, the context's variable set,
 * only sharpens the advice of some syntax errors (see the header).
 */
export function parseRaw(src: string, vars?: ExprVarSet): Result<RawProgram, ExprError> {
  return attempt(() => new Parser(src, lex(src), vars ?? null).program());
}

/** Parses an expression without an assignment prefix (used by `format`). */
export function parseRawExpression(src: string): Result<RawNode, ExprError> {
  return attempt(() => {
    const tokens = lex(src);
    if (tokens.length === 0) fail(EXPR_MSG.empty, { from: 0, to: src.length });
    return new Parser(src, tokens, null).expression();
  });
}
